import type { SwapFTData, TokenInfo, TransferFTData } from '@bitte-ai/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { http, createPublicClient, erc20Abi, formatUnits } from 'viem';
import { getRpcUrlForChain } from '../utils/evm';
import {
  calculateUsdValue,
  formatTokenAmount,
} from '../utils/numberFormatting';

// DEXScreener API interfaces
interface DexScreenerToken {
  chainId: string;
  dexId: string;
  url: string;
  pairAddress: string;
  labels?: string[];
  baseToken: {
    address: string;
    name: string;
    symbol: string;
  };
  quoteToken: {
    address: string;
    name: string;
    symbol: string;
  };
  priceNative: string;
  priceUsd: string;
  txns: {
    [timeframe: string]: {
      buys: number;
      sells: number;
    };
  };
  volume: {
    [timeframe: string]: number;
  };
  priceChange: {
    [timeframe: string]: number;
  };
  liquidity: {
    usd: number;
    base: number;
    quote: number;
  };
  fdv: number;
  marketCap?: number;
  pairCreatedAt: number;
  info?: {
    imageUrl?: string;
    websites?: {
      url: string;
    }[];
    socials?: {
      platform: string;
      handle: string;
    }[];
  };
  boosts?: {
    active: number;
  };
}

// Mapping of chain IDs to DEXScreener network identifiers
const chainToDexScreenerNetwork: Record<string, string> = {
  '1': 'ethereum',
  '10': 'optimism',
  '56': 'bsc',
  '100': 'gnosis',
  '137': 'polygon',
  '8453': 'base',
  '42161': 'arbitrum',
  '43114': 'avalanche',
  '84532': 'base', // Base Sepolia uses same DEXScreener network as Base mainnet
  // New chains - using closest equivalent networks for DEXScreener
  '130': 'unichain', // Unichain - similar to Ethereum
  '146': 'sonic', // Sonic - use ethereum as fallback
  '1868': 'soneium', // Soneium - use ethereum as fallback
  '80094': 'berachain', // Berachain - use ethereum as fallback
};

// Cache for DEXScreener results to prevent duplicate API calls
const dexScreenerCache = new Map<
  string,
  { data: { icon: string; usdPrice: number } | null; timestamp: number }
>();

// Request deduplication to prevent multiple simultaneous requests for the same token
const ongoingRequests = new Map<
  string,
  Promise<{ icon: string; usdPrice: number } | null>
>();

const CACHE_DURATION = 60000; // 1 minute cache (increased from 30 seconds)
const ERROR_CACHE_DURATION = 10000; // 10 seconds for error results

const bucketUrl = 'https://storage.googleapis.com/bitte-public';

const usdcIcon = `${bucketUrl}/usdc.png`;
const usdtIcon = `${bucketUrl}/usdt.png`;
const daiIcon = `${bucketUrl}/dai.png`;

const stablecoinFallbackData: {
  [chainId: string]: {
    [contractAddress: string]: { usdPrice: number; icon: string };
  };
} = {
  '1': {
    // Ethereum Mainnet
    '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48': {
      // USDC
      usdPrice: 1,
      icon: usdcIcon,
    },
    '0xdac17f958d2ee523a2206206994597c13d831ec7': {
      // USDT
      usdPrice: 1,
      icon: usdtIcon,
    },
    '0x6b175474e89094c44da98b954eedeac495271d0f': {
      // DAI
      usdPrice: 1,
      icon: daiIcon,
    },
  },
  '100': {
    // Gnosis Chain (xDai)
    '0xddafbb505ad214d7b80b1f830fccc89b60fb7a83': {
      // USDC (Bridged)
      usdPrice: 1,
      icon: usdcIcon,
    },
    '0x4ecaba5870353805a9f068101a40e0f32ed605c6': {
      // USDT (Bridged)
      usdPrice: 1,
      icon: usdtIcon,
    },
    '0x44fa8e6f47987339850636f88629646662444217': {
      // DAI (xDAI native)
      usdPrice: 1,
      icon: daiIcon,
    },
  },
  '137': {
    // Polygon
    '0x2791bca1f2de4661ed88a30c99a7a9449aa84174': {
      // USDC
      usdPrice: 1,
      icon: usdcIcon,
    },
    '0xc2132d05d31c914a87c6611c10748aeb04b58e8f': {
      // USDT
      usdPrice: 1,
      icon: usdtIcon,
    },
    '0x8f3cf7ad23cd3cadbd9735aff958023239c6a063': {
      // DAI
      usdPrice: 1,
      icon: daiIcon,
    },
  },
  '56': {
    // BSC
    '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d': {
      // USDC
      usdPrice: 1,
      icon: usdcIcon,
    },
    '0x55d398326f99059ff775485246999027b3197955': {
      // USDT
      usdPrice: 1,
      icon: usdtIcon,
    },
    '0x1af3f329e8be154074d8769d1ffa4ee058b1dbc3': {
      // DAI
      usdPrice: 1,
      icon: daiIcon,
    },
  },
  '42161': {
    // Arbitrum
    '0xff970a61a04b1ca14834a43f5de4533ebddb5cc8': {
      // USDC
      usdPrice: 1,
      icon: usdcIcon,
    },
    '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9': {
      // USDT
      usdPrice: 1,
      icon: usdtIcon,
    },
  },
  '43114': {
    // Avalanche
    '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E': {
      // USDC
      usdPrice: 1,
      icon: usdcIcon,
    },
    '0xc7198437980c041c805a1edcba50c1ce5db95118': {
      // USDT.e
      usdPrice: 1,
      icon: usdtIcon,
    },
  },
  '8453': {
    // Base
    '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913': {
      usdPrice: 1,
      icon: usdcIcon,
    },
    '0xfde4c96c8593536e31f229ea8f37b2ada2699bb2': {
      usdPrice: 1,
      icon: usdtIcon,
    },
  },
  '84532': {
    // Base Sepolia (testnet)
    // Note: Using common testnet stablecoin addresses for Base Sepolia
    // These addresses should be updated with actual testnet token addresses if available
    '0x036CbD53842c5426634e7929541eC2318f3dCF7e': {
      // USDC on Base Sepolia
      usdPrice: 1,
      icon: usdcIcon,
    },
  },
};

function getFallbackPrice(
  chainId?: string,
  contractAddress?: string,
): { icon: string; usdPrice: number } | null {
  if (!chainId || !contractAddress) return null;
  return (
    stablecoinFallbackData[chainId]?.[contractAddress.toLowerCase()] ?? null
  );
}

async function fetchDexScreener(
  contractAddress: string,
  chainId?: string,
): Promise<{ icon: string; usdPrice: number } | null> {
  const fallbackPrice = getFallbackPrice(chainId, contractAddress);

  if (fallbackPrice) {
    return fallbackPrice;
  }

  const cacheKey = `${chainId}-${contractAddress}`;

  // Check cache first
  const cached = dexScreenerCache.get(cacheKey);
  if (cached) {
    const isExpired = Date.now() - cached.timestamp > CACHE_DURATION;
    const isErrorCacheExpired =
      !cached.data && Date.now() - cached.timestamp > ERROR_CACHE_DURATION;

    if (!isExpired || (cached.data === null && !isErrorCacheExpired)) {
      return cached.data;
    }
  }

  // Check if there's an ongoing request for this token
  const ongoingRequest = ongoingRequests.get(cacheKey);
  if (ongoingRequest) {
    return ongoingRequest;
  }

  // Create new request
  const request = performDexScreenerRequest(contractAddress, chainId, cacheKey);
  ongoingRequests.set(cacheKey, request);

  try {
    const result = await request;
    return result;
  } finally {
    // Clean up ongoing request
    ongoingRequests.delete(cacheKey);
  }
}

async function performDexScreenerRequest(
  contractAddress: string,
  chainId: string | undefined,
  cacheKey: string,
): Promise<{ icon: string; usdPrice: number } | null> {
  try {
    if (!chainId || !chainToDexScreenerNetwork[chainId]) {
      dexScreenerCache.set(cacheKey, { data: null, timestamp: Date.now() });
      return null;
    }

    const network = chainToDexScreenerNetwork[chainId];
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    try {
      // Try primary DEXScreener endpoint
      const response = await fetch(
        `https://api.dexscreener.com/tokens/v1/${network}/${contractAddress}`,
        {
          headers: {
            Accept: 'application/json',
            'User-Agent': 'AI-Runtime-Token-Data-Fetcher',
          },
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        // If we get a CORS or other error, try fallback
        return await tryFallbackPriceSource(contractAddress, chainId, cacheKey);
      }

      const data = await response.json();
      return processDexScreenerData(data, cacheKey);
    } catch (fetchError) {
      clearTimeout(timeoutId);

      // Check if it's a CORS error
      if (
        fetchError instanceof TypeError &&
        fetchError.message.includes('CORS')
      ) {
        return await tryFallbackPriceSource(contractAddress, chainId, cacheKey);
      }

      throw fetchError;
    }
  } catch (error) {
    // Try fallback on any error
    return await tryFallbackPriceSource(contractAddress, chainId, cacheKey);
  }
}

// Function to handle potentially problematic image URLs
function sanitizeImageUrl(iconUrl: string): string {
  if (!iconUrl) return '';

  // Check if it's a DEXScreener image URL that might have CORS issues
  if (
    iconUrl.includes('dexscreener.com') ||
    iconUrl.includes('dd.dexscreener.com')
  ) {
    // For now, return empty string to use fallback instead
    // In production, you might want to proxy these through your own server
    return '';
  }

  return iconUrl;
}

// Process DEXScreener response data
function processDexScreenerData(
  data: any,
  cacheKey: string,
): { icon: string; usdPrice: number } | null {
  // Handle different response structures
  let tokens: DexScreenerToken[] = [];

  if (Array.isArray(data)) {
    tokens = data;
  } else if (
    data &&
    typeof data === 'object' &&
    'pairs' in data &&
    Array.isArray(data.pairs)
  ) {
    tokens = data.pairs;
  } else if (data && typeof data === 'object') {
    tokens = [data as DexScreenerToken];
  }

  // If no tokens are returned
  if (!tokens || tokens.length === 0) {
    dexScreenerCache.set(cacheKey, { data: null, timestamp: Date.now() });
    return null;
  }

  // Sort by liquidity to get the most liquid pair
  const sortedTokens = [...tokens].sort(
    (a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0),
  );

  // Try to find the best token with valid price data
  for (const token of sortedTokens) {
    const priceUsd = Number.parseFloat(token.priceUsd);

    // Skip tokens with invalid or suspicious prices
    if (Number.isNaN(priceUsd) || priceUsd <= 0) {
      continue;
    }

    // Only skip tokens with extremely small prices if they also have very low liquidity
    // High liquidity (>$50k) indicates the price is likely legitimate even if very small
    if (priceUsd < 0.000001 && (token.liquidity?.usd || 0) < 50000) {
      continue;
    }

    // Skip tokens with extremely high prices (> $1M) that might indicate wrong data
    if (priceUsd > 1000000) {
      continue;
    }

    let icon = '';
    if (token.info?.imageUrl) {
      // Sanitize the image URL to handle potential CORS issues
      icon = sanitizeImageUrl(token.info.imageUrl);
    }

    const result = {
      icon,
      usdPrice: priceUsd,
    };

    // Cache the successful result
    dexScreenerCache.set(cacheKey, { data: result, timestamp: Date.now() });
    return result;
  }

  // If no valid token found, cache null result
  dexScreenerCache.set(cacheKey, { data: null, timestamp: Date.now() });
  return null;
}

// Fallback price sources when DEXScreener fails
async function tryFallbackPriceSource(
  contractAddress: string,
  chainId: string | undefined,
  cacheKey: string,
): Promise<{ icon: string; usdPrice: number } | null> {
  try {
    if (!chainId) return null;

    // Try CoinGecko as fallback (for major tokens)
    const result = await fetchFromCoinGecko(contractAddress, chainId);
    if (result) {
      dexScreenerCache.set(cacheKey, { data: result, timestamp: Date.now() });
      return result;
    }

    // Try alternative DEXScreener endpoint structure
    const altResult = await tryAlternativeDexScreener(contractAddress, chainId);
    if (altResult) {
      dexScreenerCache.set(cacheKey, {
        data: altResult,
        timestamp: Date.now(),
      });
      return altResult;
    }

    // If all fail, cache null result
    dexScreenerCache.set(cacheKey, { data: null, timestamp: Date.now() });
    return null;
  } catch (error) {
    dexScreenerCache.set(cacheKey, { data: null, timestamp: Date.now() });
    return null;
  }
}

// CoinGecko fallback for major tokens
async function fetchFromCoinGecko(
  contractAddress: string,
  chainId: string,
): Promise<{ icon: string; usdPrice: number } | null> {
  try {
    const platformMap: Record<string, string> = {
      '1': 'ethereum',
      '43114': 'avalanche',
      '137': 'polygon-pos',
      '56': 'binance-smart-chain',
      '42161': 'arbitrum-one',
      '8453': 'base',
    };

    const platform = platformMap[chainId];
    if (!platform) return null;

    const response = await fetch(
      `https://api.coingecko.com/api/v3/simple/token_price/${platform}?contract_addresses=${contractAddress}&vs_currencies=usd&include_24hr_change=true`,
      {
        headers: {
          Accept: 'application/json',
        },
      },
    );

    if (!response.ok) return null;

    const data = await response.json();
    const tokenData = data[contractAddress.toLowerCase()];

    if (tokenData && tokenData.usd) {
      return {
        icon: '', // CoinGecko doesn't provide icons in this endpoint
        usdPrice: tokenData.usd,
      };
    }

    return null;
  } catch (error) {
    return null;
  }
}

// Alternative DEXScreener endpoint structure
async function tryAlternativeDexScreener(
  contractAddress: string,
  chainId: string,
): Promise<{ icon: string; usdPrice: number } | null> {
  try {
    const network = chainToDexScreenerNetwork[chainId];
    if (!network) return null;

    // Try the search endpoint instead
    const response = await fetch(
      `https://api.dexscreener.com/latest/dex/search/?q=${contractAddress}`,
      {
        headers: {
          Accept: 'application/json',
        },
      },
    );

    if (!response.ok) return null;

    const data = await response.json();
    if (data.pairs && data.pairs.length > 0) {
      // Find pair that matches our contract address
      const matchingPair = data.pairs.find(
        (pair: any) =>
          pair.baseToken?.address?.toLowerCase() ===
            contractAddress.toLowerCase() ||
          pair.quoteToken?.address?.toLowerCase() ===
            contractAddress.toLowerCase(),
      );

      if (matchingPair && matchingPair.priceUsd) {
        return {
          icon: matchingPair.info?.imageUrl || '',
          usdPrice: Number.parseFloat(matchingPair.priceUsd),
        };
      }
    }

    return null;
  } catch (error) {
    return null;
  }
}

// Enhanced function to enrich a single token with missing data
async function enrichTokenInfo(
  token: TokenInfo,
  chainId: string,
): Promise<TokenInfo> {
  const enrichedToken = { ...token };

  // Only fetch if icon or usdValue is missing
  const needsEnrichment =
    !token.icon || token.usdValue === undefined || token.usdValue === null;

  if (needsEnrichment && token.contractAddress) {
    const tokenData = await fetchDexScreener(token.contractAddress, chainId);

    // Set icon if missing - with validation
    if (!enrichedToken.icon) {
      // Always call validateAndGetTokenIcon to ensure fallback icons are used
      const validatedIcon = await validateAndGetTokenIcon(
        tokenData?.icon || '',
        token.symbol,
      );
      enrichedToken.icon = validatedIcon;
    }

    // Calculate and set usdValue if missing and we have amount
    if (
      (enrichedToken.usdValue === undefined ||
        enrichedToken.usdValue === null) &&
      enrichedToken.amount &&
      tokenData?.usdPrice
    ) {
      // Always calculate USD value - the processDexScreenerData function already filtered out truly suspicious tokens
      // Small prices can be legitimate for meme coins and micro-cap tokens
      enrichedToken.usdValue = calculateUsdValue(
        enrichedToken.amount,
        tokenData.usdPrice,
      );
    }
  }

  return enrichedToken;
}

// New function to validate and provide fallback for token icons
async function validateAndGetTokenIcon(
  iconUrl: string,
  tokenSymbol?: string,
): Promise<string> {
  // If no icon URL provided, return fallback immediately
  if (!iconUrl) {
    return getTokenIconFallback(tokenSymbol);
  }

  // Return the icon URL directly - let the browser handle loading failures
  // The TokenImage component will handle fallbacks if the image fails to load
  return iconUrl;
}

// Function to generate fallback token icons
function getTokenIconFallback(tokenSymbol?: string): string {
  // Try to generate a generic token icon based on symbol
  if (tokenSymbol) {
    const symbol = tokenSymbol.toUpperCase();

    console.log('symbol', symbol);

    // Check if we have a known token icon
    const knownTokenIcons: Record<string, string> = {
      USDC: `${bucketUrl}/usdc.png`,
      USDT: `${bucketUrl}/usdt.png`,
      DAI: `${bucketUrl}/dai.png`,
      ETH: `${bucketUrl}/eth.png`,
      WETH: `${bucketUrl}/eth.png`,
      BTC: `${bucketUrl}/btc.png`,
      WBTC: `${bucketUrl}/btc.png`,
      AVAX: `${bucketUrl}/avax.png`,
      WAVAX: `${bucketUrl}/avax.png`,
      MATIC: `${bucketUrl}/matic.png`,
      WMATIC: `${bucketUrl}/matic.png`,
      BNB: `${bucketUrl}/bnb.png`,
      WBNB: `${bucketUrl}/bnb.png`,
    };

    if (knownTokenIcons[symbol]) {
      return knownTokenIcons[symbol];
    }
  }

  // Generate a placeholder SVG
  return generatePlaceholderIcon(tokenSymbol);
}

// Function to generate a placeholder SVG icon
function generatePlaceholderIcon(tokenSymbol?: string): string {
  const symbol = tokenSymbol?.slice(0, 2).toUpperCase() || '??';
  const colors = [
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#8B5CF6',
    '#06B6D4',
    '#84CC16',
    '#F97316',
  ];

  // Use symbol to determine color consistently
  let colorIndex = 0;
  if (tokenSymbol) {
    colorIndex =
      tokenSymbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      colors.length;
  }

  const color = colors[colorIndex];

  const svg = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="12" fill="${color}"/>
      <text x="12" y="16" font-family="system-ui, -apple-system, sans-serif" font-size="9" font-weight="600" fill="white" text-anchor="middle">${symbol}</text>
    </svg>
  `;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

// Validate swap token values to ensure they make sense
function validateSwapTokenValues(
  tokenIn: TokenInfo,
  tokenOut: TokenInfo,
): boolean {
  // Skip validation if either token doesn't have USD value
  if (!tokenIn.usdValue || !tokenOut.usdValue) {
    return true;
  }

  // Calculate the ratio between token USD values
  const ratio = tokenIn.usdValue / tokenOut.usdValue;

  // Be more lenient with very small USD values (common for meme coins)
  // Only flag as suspicious if the ratio is extremely off (more than 10000x difference)
  if (ratio > 10000 || ratio < 0.0001) {
    return false;
  }

  return true;
}

export async function processSwapData(
  data: SwapFTData | undefined,
  chainId: string,
): Promise<SwapFTData | null> {
  if (!data) return null;

  const processedData = { ...data };

  if (processedData.tokenIn?.amount) {
    processedData.tokenIn.amount = formatTokenAmount(
      processedData.tokenIn.amount,
    );
  }

  if (processedData.tokenOut?.amount) {
    processedData.tokenOut.amount = formatTokenAmount(
      processedData.tokenOut.amount,
    );
  }

  // Enrich tokenIn if it exists (no balance fetching for swaps)
  if (processedData.tokenIn) {
    processedData.tokenIn = await enrichTokenInfo(
      processedData.tokenIn,
      chainId,
    );
  }

  // Enrich tokenOut if it exists (no balance fetching for swaps)
  if (processedData.tokenOut) {
    processedData.tokenOut = await enrichTokenInfo(
      processedData.tokenOut,
      chainId,
    );
  }

  // Validate the swap values to ensure they make sense
  if (processedData.tokenIn && processedData.tokenOut) {
    const isValidSwap = validateSwapTokenValues(
      processedData.tokenIn,
      processedData.tokenOut,
    );
    if (!isValidSwap) {
      // Instead of setting to undefined, we'll skip the enrichment for suspicious tokens
      // This is handled in the validateSwapTokenValues function by logging warnings
    }
  }

  return processedData;
}

export async function processTransferData(
  data: TransferFTData | undefined,
  chainId: string,
): Promise<TransferFTData | null> {
  if (!data) return null;

  const processedData = { ...data };

  if (processedData.token?.amount) {
    processedData.token.amount = formatTokenAmount(processedData.token.amount);
  }

  // Enrich token if it exists
  if (processedData.token) {
    processedData.token = await enrichTokenInfo(processedData.token, chainId);
  }

  return processedData;
}

// Hook for processing token data and enriching missing information
export function useTokenInfo<T extends SwapFTData | TransferFTData>(
  data: T | undefined,
  chainId: string,
  type: 'swap' | 'transfer-ft',
  userAddress?: string, // Optional user address for balance fetching
) {
  const [processedData, setProcessedData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<string>('0');
  const [decimals, setDecimals] = useState<number>(18);

  // Memoize the input data to prevent unnecessary re-processing
  const memoizedData = useMemo(() => {
    if (!data) return null;
    // Create a stable key that doesn't change with object references
    const key = {
      type,
      chainId,
      userAddress,
      data: data,
    };
    return JSON.stringify(key);
  }, [data, type, chainId, userAddress]);

  // Memoize the processData function to prevent unnecessary re-renders
  const processData = useCallback(async () => {
    if (!data || !chainId || !memoizedData) {
      setProcessedData(null);
      setBalance('0');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let result: T | null = null;

      if (type === 'swap') {
        result = (await processSwapData(data as SwapFTData, chainId)) as T;
      } else if (type === 'transfer-ft') {
        result = (await processTransferData(
          data as TransferFTData,
          chainId,
        )) as T;

        // Fetch balance for transfer operations if userAddress is provided
        if (
          result &&
          userAddress &&
          (result as TransferFTData).token?.contractAddress
        ) {
          const token = (result as TransferFTData).token;
          try {
            const rpcUrl = getRpcUrlForChain(Number(chainId));
            if (rpcUrl && token.contractAddress) {
              const publicClient = createPublicClient({
                transport: http(rpcUrl),
              });

              // Get token decimals
              const tokenDecimals = await publicClient.readContract({
                address: token.contractAddress as `0x${string}`,
                abi: erc20Abi,
                functionName: 'decimals',
              });
              setDecimals(Number(tokenDecimals));

              // Get token balance
              const tokenBalance = await publicClient.readContract({
                address: token.contractAddress as `0x${string}`,
                abi: erc20Abi,
                functionName: 'balanceOf',
                args: [userAddress as `0x${string}`],
              });

              const formattedBalance = formatUnits(
                tokenBalance,
                Number(tokenDecimals),
              );
              setBalance(formattedBalance);
            }
          } catch (balanceError) {
            setBalance('0');
          }
        }
      }

      setProcessedData(result);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [memoizedData, chainId, type, userAddress]);

  useEffect(() => {
    processData();
  }, [processData]);

  const retry = useCallback(() => {
    // Clear relevant cache entries on retry
    if (data) {
      const clearCacheForData = (tokenData: any) => {
        if (tokenData?.contractAddress) {
          const cacheKey = `${chainId}-${tokenData.contractAddress}`;
          dexScreenerCache.delete(cacheKey);
        }
      };

      if (type === 'swap') {
        const swapData = data as SwapFTData;
        clearCacheForData(swapData.tokenIn);
        clearCacheForData(swapData.tokenOut);
      } else if (type === 'transfer-ft') {
        const transferData = data as TransferFTData;
        clearCacheForData(transferData.token);
      }
    }

    processData();
  }, [processData, data, chainId, type]);

  return {
    data: processedData,
    loading,
    error,
    retry,
    balance, // Only relevant for transfer operations
    decimals, // Only relevant for transfer operations
    refetch: retry, // Alias for retry to match useTokenBalance API
  };
}

// Alternative hook for when you just want to enrich a single token
export function useTokenEnrichment(
  token: TokenInfo | undefined,
  chainId: string,
) {
  const [enrichedToken, setEnrichedToken] = useState<TokenInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize the input token to prevent unnecessary re-processing
  const memoizedToken = useMemo(() => {
    if (!token) return null;
    return JSON.stringify(token);
  }, [token]);

  const enrichToken = useCallback(async () => {
    if (!token || !chainId || !memoizedToken) {
      setEnrichedToken(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await enrichTokenInfo(token, chainId);
      setEnrichedToken(result);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [memoizedToken, chainId]);

  useEffect(() => {
    enrichToken();
  }, [enrichToken]);

  return {
    token: enrichedToken,
    loading,
    error,
    retry: enrichToken,
  };
}
